import type { ApiResponse } from '@evi/shared';
import { api } from './api';
import { salesDelete, salesGet } from './salesTeam';

/**
 * A Sales Partner's resale and exemption certificates, one per US state (Warren, final ruling 5, 21 October 2026, night).
 * Api: routes/admin/partner-tax.ts. The partner's account owner and EVI only; the Api refuses everyone else.
 */
export interface TaxExemptionView {
  id: string; state: string; effective_date: string; expiration_date: string | null; status: 'active' | 'removed';
  uploaded_at: string; uploaded_by: string; certificate: { filename: string; content_type: string; size: number }; recorded_with_stripe: boolean;
}
export const listTaxExemptions = () => salesGet<{ exemptions: TaxExemptionView[]; states: string[] }>('admin/partner-tax-exemptions');
export const removeTaxExemption = (id: string) => salesDelete<{ id: string; removed: boolean }>(`admin/partner-tax-exemptions/${id}`);

/** Upload one certificate: the details first, then the file (the Api reads them in that order). */
export async function addTaxExemption(file: File, meta: { state: string; expiration_date: string | null }): Promise<TaxExemptionView> {
  const form = new FormData();
  form.append('meta', JSON.stringify({ state: meta.state, expiration_date: meta.expiration_date, filename: file.name }));
  form.append('file', file, file.name);
  try {
    return (await api.post('admin/partner-tax-exemptions', { body: form, timeout: 60_000 }).json<ApiResponse<TaxExemptionView>>()).data!;
  } catch (err) {
    const response = (err as { response?: Response }).response;
    const b = response ? await response.json().catch(() => null) as { error?: { message?: string } } | null : null;
    throw new Error(b?.error?.message ?? 'The certificate did not upload.');
  }
}

/** The certificate file, saved by the browser (an <a> cannot send the sign-in header). */
export async function downloadTaxCertificate(id: string, filename: string): Promise<void> {
  const blob = await api.get(`admin/partner-tax-exemptions/${id}/certificate`).blob();
  const url = URL.createObjectURL(blob), a = document.createElement('a');
  a.href = url; a.download = filename || 'certificate';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
